import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import RichTextEditor from "../ReusableComponent/RichTextEditor";
import {
  fetchSectionById,
  fetchTopicById, fetchModuleById, fetchSubjectById2,
  updateSectionNotesText,
  addQuestionToSection, removeQuestionFromSection,
} from "../../pages/services/notesService";
import AssignmentQuestionPanel from "../ReusableComponent/AssignmentQuestionPanel";
import "../../styles/CourseNotes.css";
import "../../styles/ListPage.css";

const TYPE_LABELS = {
  notes:      "Notes",
  example:    "Example Questions",
  quiz:       "Quiz",
  practice:   "Practice Questions",
  assignment: "Assignment Questions",
};

const DiffBadge = ({ level }) => {
  if (!level) return null;
  const cls = `cn-q-badge cn-q-diff cn-diff-${level.toLowerCase().replace(/\s+/g, "-")}`;
  return <span className={cls}>{level}</span>;
};

const SectionContentPage = () => {
  const { subjectId, topicId, sectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [section,      setSection]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [topicTitle,   setTopicTitle]   = useState("");
  const [moduleTitle,  setModuleTitle]  = useState("");
  const [subjectTitle, setSubjectTitle] = useState("");

  // Notes-section state
  const [notesText,    setNotesText]    = useState("");
  const notesEditorRef = useRef(null); // ref to RichTextEditor — read .getHTML() at save time
  const [saving,       setSaving]       = useState(false);
  const [savedOk,      setSavedOk]      = useState(false);

  // Question-section state
  const [sliderOpen,   setSliderOpen]   = useState(false);

  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  useEffect(() => {
    if (location.state?.successMessage) {
      showToast(location.state.successMessage);
      window.history.replaceState({}, "");
    }
  }, []);

  useEffect(() => {
    fetchSectionById(sectionId)
      .then((s) => { setSection(s); setNotesText(s?.notesText || ""); })
      .catch(() => {})
      .finally(() => setLoading(false));

    fetchTopicById(topicId).then((t) => {
      setTopicTitle(t?.title || "Topic");
      if (t?.moduleId) fetchModuleById(t.moduleId).then((m) => setModuleTitle(m?.title || "")).catch(() => {});
    }).catch(() => {});
    fetchSubjectById2(subjectId).then((s) => setSubjectTitle(s?.title || "")).catch(() => {});
  }, [sectionId, topicId, subjectId]);

  const handleSaveNotes = async () => {
    setSaving(true);
    setSavedOk(false);
    try {
      // Read directly from TipTap editor via ref — guaranteed latest content
      const content = notesEditorRef.current?.getHTML() ?? notesText;
      await updateSectionNotesText(sectionId, content);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);
    } catch { showToast("Failed to save. Please try again."); }
    setSaving(false);
  };

  const handleConfirmQuestions = async (selectedIds, removedIds) => {
    const existingIds = (section?.questions || []).map((q) => q._id);
    const toAdd = selectedIds.filter((id) => !existingIds.includes(id));
    try {
      let updated = section;
      for (const id of toAdd) {
        const res = await addQuestionToSection(sectionId, id);
        updated = res.data;
      }
      for (const id of removedIds) {
        const res = await removeQuestionFromSection(sectionId, id);
        updated = res.data;
      }
      setSection(updated);
      showToast("Questions updated");
    } catch { showToast("Error updating questions"); }
  };

  const handleRemoveQuestion = async (questionId) => {
    try {
      const res = await removeQuestionFromSection(sectionId, questionId);
      setSection(res.data);
      showToast("Question removed");
    } catch { showToast("Error removing question"); }
  };

  const topicPath = `/notes/subjects/${subjectId}/topics/${topicId}`;
  const sectionLabel = section ? (TYPE_LABELS[section.type] || section.type) : "";
  const addedQuestions = section?.questions || [];
  const addedQIds = addedQuestions.map((q) => q._id);

  if (loading) {
    return <div className="cn-page"><div className="cn-loading" style={{ paddingTop: 60 }}>Loading…</div></div>;
  }

  return (
    <div className="cn-page">
      {toast && <div className="cn-toast">{toast}</div>}

      {/* Breadcrumb */}
      <div className="cn-breadcrumb">
        <span className="cn-breadcrumb-link" onClick={() => navigate("/notes")}>Notes</span>
        {subjectTitle && (
          <>
            <span className="cn-breadcrumb-sep"> › </span>
            <span className="cn-breadcrumb-link" onClick={() => navigate(`/notes/subjects/${subjectId}`)}>
              Subject: {subjectTitle}
            </span>
          </>
        )}
        {moduleTitle && (
          <>
            <span className="cn-breadcrumb-sep"> › </span>
            <span className="cn-breadcrumb-link" onClick={() => navigate(`/notes/subjects/${subjectId}`)}>
              Module: {moduleTitle}
            </span>
          </>
        )}
        {topicTitle && (
          <>
            <span className="cn-breadcrumb-sep"> › </span>
            <span className="cn-breadcrumb-link" onClick={() => navigate(topicPath)}>
              Topic: {topicTitle}
            </span>
          </>
        )}
        <span className="cn-breadcrumb-sep"> › </span>
        <span className="cn-breadcrumb-current">{section?.title || "Section"}</span>
      </div>

      <div className="cn-header">
        <div>
          <h2 className="cn-title">{section?.title || "Section"}</h2>
          {sectionLabel && (
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "#64748b" }}>
              Type: <strong>{sectionLabel}</strong>
            </p>
          )}
        </div>
        <button className="cn-back-btn" onClick={() => navigate(topicPath)}>
          ← Back to Sections
        </button>
      </div>

      {/* ── Notes Section ── */}
      {section?.type === "notes" && (
        <div className="cn-form-card" style={{ marginTop: 20 }}>
          <RichTextEditor
            ref={notesEditorRef}
            value={notesText}
            onBlur={(val) => setNotesText(val)}
            toolbar="full"
            height={560}
            placeholder="Write your notes here..."
          />
          <div className="cn-form-actions" style={{ marginTop: 16 }}>
            <div style={{ flex: 1 }} />
            {savedOk && (
              <span style={{ color: "#16a34a", fontSize: 13, fontWeight: 600, alignSelf: "center" }}>
                ✓ Saved
              </span>
            )}
            <button
              className="cn-save-btn-lg"
              onClick={handleSaveNotes}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Notes"}
            </button>
          </div>
        </div>
      )}

      {/* ── Question Section (example / quiz / practice / assignment) ── */}
      {section && section.type !== "notes" && (
        <div className="cn-tab-content">
          <div className="cn-tab-toolbar">
            <span className="cn-tab-count">
              {addedQuestions.length} question{addedQuestions.length !== 1 ? "s" : ""}
            </span>
            <button className="cn-primary-btn" onClick={() => setSliderOpen(true)}>
              Manage Questions
            </button>
          </div>

          {addedQuestions.length === 0 ? (
            <p className="cn-empty">No questions added yet. Click "Manage Questions" to add some.</p>
          ) : (
            <div className="cn-added-qs">
              {addedQuestions.map((q, i) => (
                <div key={q._id} className="cn-q-row cn-q-added">
                  <div className="cn-q-info">
                    <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                      <span className="cn-q-num">{i + 1}.</span>
                      <span className="cn-q-text">{q.question}</span>
                    </div>
                    <div className="cn-q-meta">
                      {q.subject && <span className="cn-q-badge cn-q-subject">{q.subject}</span>}
                      <DiffBadge level={q.difficultyLevel} />
                    </div>
                  </div>
                  <button className="cn-del-btn" onClick={() => handleRemoveQuestion(q._id)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AssignmentQuestionPanel
        open={sliderOpen}
        onClose={() => setSliderOpen(false)}
        onConfirm={handleConfirmQuestions}
        existingIds={addedQIds}
        existingObjects={addedQuestions}
      />
    </div>
  );
};

export default SectionContentPage;
